import {isNotNil} from 'ramda';

import type {
  CheckoutProfileDocumentCell,
  CheckoutProfileDocumentRow,
  CheckoutProfileWrite,
  SaleRegime,
} from '../types';
import {
  getCheckoutProfileDocumentCells,
  getCheckoutProfileDocumentCellKey,
} from './getCheckoutProfileDocumentCells';

// A cell without a row is unrestricted, so a copy of it clears the restrictions of the target row.
const copyDocumentRow = (
  row: CheckoutProfileDocumentRow | undefined,
  cell: CheckoutProfileDocumentCell
): CheckoutProfileDocumentRow =>
  isNotNil(row)
    ? {
        ...row,
        ...cell,
        allowedSeriesIds: [...row.allowedSeriesIds],
        allowedTemplateIds: [...row.allowedTemplateIds],
      }
    : {
        ...cell,
        allowedSeriesIds: [],
        defaultSeriesId: null,
        allowedTemplateIds: [],
        defaultTemplateId: null,
        isOverrideAllowed: false,
      };

export function copyCheckoutProfileRegime(
  profile: CheckoutProfileWrite,
  source: SaleRegime,
  target: SaleRegime
): CheckoutProfileWrite {
  const cells = getCheckoutProfileDocumentCells(profile);
  const applicable = new Set(cells.map(getCheckoutProfileDocumentCellKey));
  const rows = new Map(
    profile.documents.map((row) => [getCheckoutProfileDocumentCellKey(row), row])
  );

  const copied = cells
    .filter((cell) => cell.saleRegime === source)
    .map((cell) => ({...cell, saleRegime: target}))
    .filter((cell) => applicable.has(getCheckoutProfileDocumentCellKey(cell)))
    .map((cell) =>
      copyDocumentRow(
        rows.get(getCheckoutProfileDocumentCellKey({...cell, saleRegime: source})),
        cell
      )
    );

  const replaced = new Set(copied.map(getCheckoutProfileDocumentCellKey));

  return {
    ...profile,
    documents: [
      ...profile.documents.filter((row) => !replaced.has(getCheckoutProfileDocumentCellKey(row))),
      ...copied,
    ],
  };
}
